import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Camera, Building2, Eye, EyeOff } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { openExternalLink } from '@/components/utils/openExternalLink';
import { mensajeError } from '@/components/utils/mensajeError';
import { PASSWORD_MIN, passwordValida } from '@/components/utils/password';
import PasswordRequisitos from '@/components/ui/PasswordRequisitos';

import { useEmpresaRegistro } from '@/components/hooks/useEmpresaRegistro';
import { useCategoriasEmpresa } from '@/components/hooks/useCategoriasEmpresa';
import { useSubcategorias } from '@/components/hooks/useSubcategorias';
import { logoutUser } from '@/components/utils/logoutUser';
import {
  cargarPromocionActiva,
  formatearTextoPromocion,
  type PromocionActiva,
} from '@/components/hooks/usePromocion';

import LogoCropDialog from '@/components/pages/dialogs/LogoCropDialog';
import SubcategoriaMultiSelect from '@/components/ui/SubcategoriaMultiSelect';
import HeadSEO from '@/components/seo/HeadSEO';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';

export default function CompanyRegister() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [isGoogle, setIsGoogle] = useState(false);
  const [promo, setPromo] = useState<PromocionActiva | null>(null);

  const {
    step,
    setStep,
    consentimiento,
    setConsentimiento,
    verificarEmail,
    registrarEmpresaCompleto,
  } = useEmpresaRegistro();

  const [empresaData, setEmpresaData] = useState({
    empresa: '',
    id_categoria: '',
    descripcion: '',
    pais: '',
    ciudad: '',
    localidad: '',
    direccion: '',
    telefono: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  // ✅ El logo se guarda en una ref para evitar problemas de closure/stale state
  const logoRef = useRef<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  const [rawLogo, setRawLogo] = useState<File | null>(null);
  const [openLogoCrop, setOpenLogoCrop] = useState(false);

  const { categorias } = useCategoriasEmpresa();

  // Subcategorías: opcionales, dependientes de la categoría elegida
  const [subcategoriasSeleccionadas, setSubcategoriasSeleccionadas] = useState<
    string[]
  >([]);
  const { subcategorias } = useSubcategorias(empresaData.id_categoria || null);

  const [registrando, setRegistrando] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    const ctx = localStorage.getItem('oauth_context');
    const googleEmail = localStorage.getItem('google_email');
    if (ctx === 'companyRegister' && googleEmail) {
      setIsGoogle(true);
      setEmpresaData((p) => ({ ...p, email: googleEmail }));
      setStep(2);
    }
  }, [setStep]);

  // Promo activa para el subtítulo del hero (se registra con la que esté
  // vigente en ese momento: lanzamiento, trial de 14 días, etc.)
  useEffect(() => {
    cargarPromocionActiva().then(setPromo);
  }, []);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { id, value } = e.target;
    if (e.target instanceof HTMLInputElement && e.target.type === 'file') {
      const file = e.target.files?.[0];
      if (!file) return;
      setRawLogo(file);
      setOpenLogoCrop(true);
      // Resetear el input para permitir seleccionar el mismo archivo de nuevo
      e.target.value = '';
      return;
    }
    setEmpresaData((p) => ({ ...p, [id]: value }));
  };

  const handleLogoCropped = (file: File) => {
    // ✅ Guardar en ref (inmediato, sin re-render) y en preview (para mostrar)
    logoRef.current = file;
    setLogoPreview(URL.createObjectURL(file));
    setRawLogo(null);
    setOpenLogoCrop(false);
  };

  const handleCategoriaChange = (value: string) => {
    setEmpresaData((p) => ({ ...p, id_categoria: value }));
    // Si cambia la categoría, las subcategorías de la categoría anterior ya no aplican
    setSubcategoriasSeleccionadas([]);
  };

  const paso1OK =
    empresaData.email.trim() &&
    (isGoogle ||
      (passwordValida(empresaData.password) &&
        empresaData.password === empresaData.confirmPassword));

  const paso2OK = empresaData.empresa && empresaData.id_categoria;
  const paso3OK =
    empresaData.pais &&
    empresaData.ciudad &&
    empresaData.localidad &&
    empresaData.direccion &&
    empresaData.telefono;

  const handleNextPaso1 = async () => {
    const email = empresaData.email.trim().toLowerCase();
    if (!email) {
      toast.error(t('registerCompany.errorEmail'));
      return;
    }
    if (!isGoogle) {
      if (!empresaData.password || !empresaData.confirmPassword) {
        toast.error(t('registerCompany.errorPassword'));
        return;
      }
      if (!passwordValida(empresaData.password)) {
        toast.error(t('password.errorWeak', { min: PASSWORD_MIN }));
        return;
      }
      if (empresaData.password !== empresaData.confirmPassword) {
        toast.error(t('password.errorMatch'));
        return;
      }
    }
    // La verificación de email va después de validar la contraseña: así no
    // gastamos una llamada al servidor si el formulario aún no es válido.
    const resp = await verificarEmail(email);
    if (!resp.ok) {
      toast.error(mensajeError(resp.error) || t('registerCompany.errorEmail'));
      return;
    }
    setStep(2);
  };

  const handleRegistrar = async () => {
    setRegistrando(true);
    try {
      const resp = await registrarEmpresaCompleto(
        {
          ...empresaData,
          logo: logoRef.current,
          subcategorias: subcategoriasSeleccionadas,
        },
        navigate
      );
      if (!resp.ok) {
        // Mostrar el motivo real si el hook lo propaga; el genérico solo como fallback
        toast.error(
          mensajeError(resp.error) || t('registerCompany.errorRegister')
        );
        return;
      }
      // Limpiamos el contexto de OAuth para que un futuro registro
      // no salte directamente al paso 2 por datos antiguos
      localStorage.removeItem('oauth_context');
      localStorage.removeItem('google_email');
      toast.success(t('registerCompany.successRegister'));
      navigate('/panel-empresa', { replace: true });
    } finally {
      setRegistrando(false);
    }
  };

  const steps = [
    t('registerCompany.stepCredentials'),
    t('registerCompany.stepBasicInfo'),
    t('registerCompany.stepContact'),
  ];

  // ── Botones de navegación entre pasos ──────────────────────────────────
  // Se calculan una sola vez aquí y se pintan en DOS sitios:
  // 1) Inline dentro de la tarjeta, solo en escritorio (sm:flex).
  // 2) En una barra fija al fondo de la pantalla, solo en móvil, montada vía
  //    portal directamente en document.body — así el position:fixed no se
  //    rompe por el transform del motion.div de la tarjeta (Framer Motion
  //    deja un `transform` inline tras animar, que crea un containing block
  //    y convertiría el fixed en un absolute atrapado dentro de la tarjeta).
  const BTN_BASE =
    'h-12 sm:h-11 text-base sm:text-sm active:scale-[0.98] transition-transform ' +
    'disabled:opacity-40 disabled:grayscale disabled:cursor-not-allowed disabled:active:scale-100';
  const BTN_PRIMARY = `bg-[#103254] dark:bg-[#288E7F] text-white hover:bg-[#1a4a6e] dark:hover:bg-[#1e7a6e] disabled:hover:bg-[#103254] dark:disabled:hover:bg-[#288E7F] ${BTN_BASE}`;
  const BTN_OUTLINE = `dark:bg-white/5 dark:border-white/10 dark:text-white ${BTN_BASE}`;

  let footerButtons: React.ReactNode;
  if (step === 1) {
    footerButtons = (
      <Button
        className={`w-full ${BTN_PRIMARY}`}
        disabled={!paso1OK}
        onClick={handleNextPaso1}
      >
        {t('registerCompany.next')}
      </Button>
    );
  } else if (step === 2) {
    footerButtons = (
      <>
        <Button
          variant="outline"
          className={`flex-1 ${BTN_OUTLINE}`}
          onClick={() => setStep(1)}
        >
          {t('registerCompany.back')}
        </Button>
        <Button
          className={`flex-1 ${BTN_PRIMARY}`}
          disabled={!paso2OK}
          onClick={() =>
            paso2OK ? setStep(3) : toast.error(t('registerCompany.errorFields'))
          }
        >
          {t('registerCompany.next')}
        </Button>
      </>
    );
  } else {
    footerButtons = (
      <>
        <Button
          variant="outline"
          className={`flex-1 ${BTN_OUTLINE}`}
          onClick={() => setStep(2)}
        >
          {t('registerCompany.back')}
        </Button>
        <Button
          className={`flex-1 ${BTN_PRIMARY}`}
          disabled={!consentimiento || !paso3OK || registrando}
          onClick={handleRegistrar}
        >
          {registrando ? (
            <span className="flex items-center gap-2">
              <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              Registrando...
            </span>
          ) : (
            t('registerCompany.register')
          )}
        </Button>
      </>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-white dark:bg-black">
      <HeadSEO
        title="Registra tu negocio — Bookoll"
        description="Crea tu perfil de empresa en Bookoll. Gestiona citas, servicios y clientes desde un solo lugar."
        canonical="https://bookoll.com/register-company"
      />

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section className="relative z-0 overflow-hidden text-white w-full">
        {/* Fondo modo claro */}
        <div
          className="absolute inset-0 dark:hidden"
          style={{
            background: `
              radial-gradient(ellipse at 15% 60%, rgba(40,142,127,0.22) 0%, transparent 50%),
              radial-gradient(ellipse at 85% 20%, rgba(16,50,84,0.5) 0%, transparent 55%),
              linear-gradient(170deg, #103254 0%, #103254 25%, #1a4a6e 45%, #3d7fa0 68%, #a8cfe0 84%, #e8f4f8 95%, #ffffff 100%)
            `,
          }}
        />
        {/* Fondo modo oscuro: negro con resplandor teal, igual que Login */}
        <div
          className="absolute inset-0 hidden dark:block"
          style={{
            background: `
              radial-gradient(ellipse at 15% 55%, rgba(40, 142, 127, 0.35) 0%, transparent 55%),
              radial-gradient(ellipse at 85% 15%, rgba(16, 50, 84, 0.45) 0%, transparent 55%),
              linear-gradient(170deg, #050807 0%, #0a1a16 22%, #123a30 42%, #103254 60%, #0a0f14 82%, #000000 100%)
            `,
          }}
        />

        {/* Orb izquierda */}
        <motion.div
          className="absolute top-10 left-[5%] w-40 h-40 sm:w-56 sm:h-56 rounded-full pointer-events-none"
          style={{
            background:
              'radial-gradient(circle, rgba(40,142,127,0.18) 0%, transparent 70%)',
            filter: 'blur(40px)',
          }}
          animate={{ scale: [1, 1.1, 1], opacity: [0.5, 0.9, 0.5] }}
          transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
        />
        {/* Orb derecha */}
        <motion.div
          className="absolute top-4 right-[8%] w-48 h-48 sm:w-64 sm:h-64 rounded-full pointer-events-none"
          style={{
            background:
              'radial-gradient(circle, rgba(255,255,255,0.07) 0%, transparent 70%)',
            filter: 'blur(50px)',
          }}
          animate={{ scale: [1, 1.08, 1], opacity: [0.4, 0.75, 0.4] }}
          transition={{
            duration: 9,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 3,
          }}
        />

        {/* Botón volver */}
        <button
          onClick={async () => {
            if (isGoogle) {
              await logoutUser(); // ya limpia oauth_context y google_email
            } else {
              localStorage.removeItem('oauth_context');
              localStorage.removeItem('google_email');
            }
            navigate('/');
          }}
          aria-label="Volver atrás"
          className="absolute left-4 sm:top-5 sm:left-5 z-20 flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-3.5 py-2 sm:px-4 text-sm text-white active:scale-95 hover:bg-white/20 transition-all"
          style={{ top: 'calc(env(safe-area-inset-top) + 1rem)' }}
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="hidden sm:inline font-medium">Volver</span>
        </button>

        {/* Contenido hero */}
        <div className="relative z-10 pt-16 sm:pt-20 pb-16 sm:pb-28 px-6 flex flex-col items-center text-center max-w-3xl mx-auto">
          <motion.div
            className="mb-4 sm:mb-5 w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center shadow-lg"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Building2 className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
          </motion.div>

          <motion.h1
            className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut', delay: 0.1 }}
          >
            {t('registerCompany.title')}
          </motion.h1>

          <motion.div
            className="mt-3 sm:mt-4 h-px w-16 mx-auto"
            style={{
              background:
                'linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)',
            }}
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.7, delay: 0.5 }}
          />

          {promo && (
            <motion.p
              className="mt-3 text-xs sm:text-sm text-white/70 max-w-xl leading-relaxed px-2"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: 'easeOut', delay: 0.3 }}
            >
              {formatearTextoPromocion(promo)}
            </motion.p>
          )}

          {/* Stepper */}
          <motion.div
            className="mt-6 sm:mt-8 flex items-center gap-1.5 sm:gap-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            {steps.map((label, i) => (
              <div key={i} className="flex items-center gap-1.5 sm:gap-2">
                <div className="flex flex-col items-center gap-1">
                  <div
                    className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${
                      i + 1 === step
                        ? 'bg-white text-[#103254] shadow-lg scale-110'
                        : i + 1 < step
                        ? 'bg-[#288E7F] text-white'
                        : 'bg-white/20 text-white/50'
                    }`}
                  >
                    {i + 1 < step ? '✓' : i + 1}
                  </div>
                  <span
                    className={`text-[10px] hidden sm:block transition-colors ${
                      i + 1 === step
                        ? 'text-white font-medium'
                        : 'text-white/40'
                    }`}
                  >
                    {label}
                  </span>
                </div>
                {i < steps.length - 1 && (
                  <div
                    className={`w-8 sm:w-10 h-px mb-4 transition-colors ${
                      i + 1 < step ? 'bg-[#288E7F]' : 'bg-white/20'
                    }`}
                  />
                )}
              </div>
            ))}
          </motion.div>
        </div>

        {/* Wave */}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none pointer-events-none">
          <svg
            viewBox="0 0 1440 60"
            preserveAspectRatio="none"
            className="w-full h-8 sm:h-14"
          >
            <path
              d="M0,30 C360,60 1080,0 1440,30 L1440,60 L0,60 Z"
              className="fill-white dark:fill-black"
            />
          </svg>
        </div>
      </section>

      {/* ── Card ──────────────────────────────────────────────────────────── */}
      <div className="relative z-10 flex justify-center px-3 sm:px-4 -mt-6 sm:-mt-10 pb-8 sm:pb-16">
        <motion.div
          key={step}
          className="w-full max-w-lg bg-white dark:bg-[#1c1c1e] rounded-lg shadow-2xl border sm:border-2 border-gray-200 dark:border-white/10 p-5 sm:p-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          {/* Paso 1 */}
          {step === 1 && (
            <div className="space-y-5">
              <div>
                <Label className="dark:text-white/90">
                  {t('registerCompany.email')}{' '}
                  <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={empresaData.email}
                  onChange={handleChange}
                  disabled={isGoogle}
                  placeholder={t('registerCompany.emailPlaceholder')}
                  className="mt-1 h-11 dark:bg-white/5 dark:border-white/10 dark:text-white"
                />
              </div>

              {!isGoogle && (
                <>
                  <div>
                    <Label className="dark:text-white/90">
                      {t('registerCompany.password')}{' '}
                      <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative mt-1">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        value={empresaData.password}
                        onChange={handleChange}
                        placeholder={t('password.placeholder')}
                        className="h-11 pr-10 dark:bg-white/5 dark:border-white/10 dark:text-white"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        tabIndex={-1}
                        aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-white/40 hover:text-gray-600 dark:hover:text-white/70"
                      >
                        {showPassword ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                    </div>

                    <PasswordRequisitos password={empresaData.password} />
                  </div>
                  <div>
                    <Label className="dark:text-white/90">
                      {t('registerCompany.confirmPassword')}{' '}
                      <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative mt-1">
                      <Input
                        id="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        value={empresaData.confirmPassword}
                        onChange={handleChange}
                        placeholder={t(
                          'registerCompany.confirmPasswordPlaceholder'
                        )}
                        className="h-11 pr-10 dark:bg-white/5 dark:border-white/10 dark:text-white"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword((v) => !v)}
                        tabIndex={-1}
                        aria-label={
                          showConfirmPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'
                        }
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-white/40 hover:text-gray-600 dark:hover:text-white/70"
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                    {empresaData.confirmPassword.length > 0 &&
                      empresaData.password !== empresaData.confirmPassword && (
                        <p className="mt-2 text-xs text-red-500">
                          {t('password.errorMatch')}
                        </p>
                      )}
                  </div>
                </>
              )}

            </div>
          )}

          {/* Paso 2 */}
          {step === 2 && (
            <div className="space-y-5">
              <div className="flex flex-col items-center gap-2">
                <label
                  htmlFor="logo"
                  className="w-24 h-24 rounded-full border-2 border-dashed border-[#103254]/30 dark:border-white/20 flex items-center justify-center cursor-pointer bg-gray-50 dark:bg-white/5 active:scale-95 hover:bg-gray-100 dark:hover:bg-white/10 transition-all overflow-hidden"
                >
                  {logoPreview ? (
                    <img
                      src={logoPreview}
                      alt="logo"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Camera className="w-6 h-6 text-[#103254]/40 dark:text-white/40" />
                  )}
                </label>
                <span className="text-xs text-gray-400 dark:text-white/40">
                  {t('registerCompany.logoLabel')} (opcional)
                </span>
                <input
                  id="logo"
                  type="file"
                  accept="image/*"
                  onChange={handleChange}
                  className="hidden"
                />
              </div>

              <div>
                <Label className="dark:text-white/90">
                  {t('registerCompany.companyName')}{' '}
                  <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="empresa"
                  autoComplete="organization"
                  value={empresaData.empresa}
                  onChange={handleChange}
                  placeholder={t('registerCompany.companyNamePlaceholder')}
                  className="mt-1 h-11 dark:bg-white/5 dark:border-white/10 dark:text-white"
                />
              </div>

              <div>
                <Label className="dark:text-white/90">
                  {t('registerCompany.category')}{' '}
                  <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={empresaData.id_categoria}
                  onValueChange={handleCategoriaChange}
                >
                  <SelectTrigger className="mt-1 h-11 dark:bg-white/5 dark:border-white/10 dark:text-white focus:ring-[#103254]/30">
                    <SelectValue
                      placeholder={t('registerCompany.categoryPlaceholder')}
                    />
                  </SelectTrigger>
                  <SelectContent className="z-[70]">
                    {categorias.map((c) => (
                      <SelectItem
                        key={c.id_categoria}
                        value={String(c.id_categoria)}
                      >
                        {c.categoria}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-0.5 mb-1">
                  <Label className="dark:text-white/90">Subcategorías</Label>
                  <span className="text-xs text-gray-400 dark:text-white/40">
                    Opcional — ayuda a que te encuentren mejor
                  </span>
                </div>
                <SubcategoriaMultiSelect
                  opciones={subcategorias}
                  seleccionadas={subcategoriasSeleccionadas}
                  onChange={setSubcategoriasSeleccionadas}
                  disabled={!empresaData.id_categoria}
                />
              </div>

              <div>
                <Label className="dark:text-white/90">
                  {t('registerCompany.description')}{' '}
                  <span className="text-gray-400 dark:text-white/40 font-normal">
                    (Opcional)
                  </span>
                </Label>
                <Textarea
                  id="descripcion"
                  value={empresaData.descripcion}
                  onChange={handleChange}
                  placeholder={t('registerCompany.descriptionPlaceholder')}
                  className="mt-1 dark:bg-white/5 dark:border-white/10 dark:text-white"
                />
              </div>

            </div>
          )}

          {/* Paso 3 */}
          {step === 3 && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="dark:text-white/90">
                    {t('registerCompany.country')}{' '}
                    <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="pais"
                    value={empresaData.pais}
                    onChange={handleChange}
                    placeholder={t('registerCompany.countryPlaceholder')}
                    className="mt-1 h-11 dark:bg-white/5 dark:border-white/10 dark:text-white"
                  />
                </div>
                <div>
                  <Label className="dark:text-white/90">
                    {t('registerCompany.city')}{' '}
                    <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="ciudad"
                    value={empresaData.ciudad}
                    onChange={handleChange}
                    placeholder={t('registerCompany.cityPlaceholder')}
                    className="mt-1 h-11 dark:bg-white/5 dark:border-white/10 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <Label className="dark:text-white/90">
                  {t('registerCompany.locality')}{' '}
                  <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="localidad"
                  value={empresaData.localidad}
                  onChange={handleChange}
                  placeholder={t('registerCompany.localityPlaceholder')}
                  className="mt-1 h-11 dark:bg-white/5 dark:border-white/10 dark:text-white"
                />
              </div>

              <div>
                <Label className="dark:text-white/90">
                  {t('registerCompany.address')}{' '}
                  <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="direccion"
                  value={empresaData.direccion}
                  onChange={handleChange}
                  placeholder={t('registerCompany.addressPlaceholder')}
                  className="mt-1 h-11 dark:bg-white/5 dark:border-white/10 dark:text-white"
                />
              </div>

              <div>
                <Label className="dark:text-white/90">
                  {t('registerCompany.phone')}{' '}
                  <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="telefono"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  value={empresaData.telefono}
                  onChange={handleChange}
                  placeholder={t('registerCompany.phonePlaceholder')}
                  className="mt-1 h-11 dark:bg-white/5 dark:border-white/10 dark:text-white"
                />
              </div>

              <div className="flex items-start gap-3 p-4 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10">
                <Switch
                  checked={consentimiento}
                  onCheckedChange={setConsentimiento}
                  className="mt-0.5"
                />
                <Label className="text-xs leading-relaxed text-gray-500 dark:text-white/50">
                  {t('registerCompany.consent')}{' '}
                  <button
                    type="button"
                    onClick={() => openExternalLink('https://bookoll.com/terms')}
                    className="underline text-[#103254] dark:text-[#4fb8a3] font-medium text-xs leading-relaxed"
                  >
                    {t('registerCompany.terms')}
                  </button>{' '}
                  {t('registerCompany.and')}{' '}
                  <button
                    type="button"
                    onClick={() => openExternalLink('https://bookoll.com/privacy-policy')}
                    className="underline text-[#103254] dark:text-[#4fb8a3] font-medium text-xs leading-relaxed"
                  >
                    {t('registerCompany.privacy')}
                  </button>
                </Label>
              </div>

            </div>
          )}

          {/* Footer de botones: sticky al fondo DE LA TARJETA (no de toda la
              pantalla), para que se sienta parte del formulario y no una
              barra suelta. Se queda visible mientras hay tarjeta debajo, y
              deja de pegarse en cuanto se llega al final de la tarjeta. */}
          <div
            className="sticky bottom-0 -mx-5 sm:-mx-8 -mb-5 sm:-mb-8 mt-5 px-5 sm:px-8 py-4 flex gap-3 bg-white/95 dark:bg-[#1c1c1e]/95 backdrop-blur-sm border-t border-gray-100 dark:border-white/10 rounded-b-lg"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}
          >
            {footerButtons}
          </div>
        </motion.div>
      </div>

      {rawLogo && (
        <LogoCropDialog
          open={openLogoCrop}
          file={rawLogo}
          onCancel={() => {
            setRawLogo(null);
            setOpenLogoCrop(false);
          }}
          onSave={handleLogoCropped}
        />
      )}
    </div>
  );
}